import { TrendingDown, TrendingUp } from 'lucide-react';
import {
  formatPctChangeLabel,
  percentChangeVsBaseline,
} from '@/api/snapshotMetrics';
import { cn } from '@/utils/cn';

type MetricPolarity = 'higher_is_better' | 'lower_is_better' | 'neutral';

export function SnapshotChangePill({
  current,
  previous,
  polarity = 'higher_is_better',
  show = true,
  variant = 'pill',
}: {
  current: number;
  previous: number;
  polarity?: MetricPolarity;
  show?: boolean;
  variant?: 'pill' | 'plain' | 'compact';
}) {
  if (!show) return null;

  const pct = percentChangeVsBaseline(current, previous);
  const label = formatPctChangeLabel(pct);
  const compact = variant === 'compact';
  const plain = variant === 'plain' || compact;
  const sizeClass = compact ? 'text-[10px] font-light' : 'text-xs font-medium';

  if (pct.type === 'baseline_zero') {
    return <span className={cn(sizeClass, 'text-foreground-muted')}>—</span>;
  }
  if (pct.type === 'both_zero') {
    return (
      <span
        className={cn(
          'inline-flex items-center text-foreground-muted',
          plain ? sizeClass : 'rounded-full bg-muted/70 px-2.5 py-0.5 text-xs font-semibold'
        )}
      >
        0%
      </span>
    );
  }

  const v = pct.value;
  const isUp = v > 0;
  const isGood =
    polarity === 'neutral' ? isUp : polarity === 'higher_is_better' ? isUp : !isUp;

  if (isGood) {
    return (
      <span
        className={cn(
          'inline-flex items-center text-success-ink',
          plain
            ? cn('gap-1.5', sizeClass)
            : 'gap-1 rounded-full bg-success-subtle px-2.5 py-0.5 text-xs font-semibold'
        )}
      >
        <TrendingUp className="h-3 w-3 shrink-0" aria-hidden />
        {label}
      </span>
    );
  }
  // Bad for polarity (e.g. CPC/ACoS/Spend up, or ROAS/CTR down) — same error ink as other columns.
  if (v !== 0) {
    const TrendIcon = isUp ? TrendingUp : TrendingDown;
    return (
      <span
        className={cn(
          'inline-flex items-center text-error-ink',
          plain
            ? cn('gap-1.5', sizeClass)
            : 'gap-1 rounded-full bg-error-subtle px-2.5 py-0.5 text-xs font-semibold'
        )}
      >
        <TrendIcon className="h-3 w-3 shrink-0" aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center text-foreground-muted',
        plain ? sizeClass : 'rounded-full bg-muted/70 px-2.5 py-0.5 text-xs font-semibold'
      )}
    >
      {label}
    </span>
  );
}
